import { useQuery } from '@tanstack/react-query';

import { ICON_URL_STALE_TIME } from '../../app/constants';
import { queryClient } from '../../api/queryClient';
import {
  getV1BusinessPartnerCurrentOptions as getPapBusinessPartnerCurrentOptions,
  getV1BusinessPartnerCurrentQueryKey as getPapBusinessPartnerCurrentQueryKey,
  getV1IdentityCurrentBusinessPartnersOptions,
  getV1IdentityCurrentBusinessPartnersQueryKey,
} from '../../api/generated/pap/@tanstack/react-query.gen';
import { getV1BusinessPartnerCurrentChange } from '../../api/generated/pm';
import {
  getV1BusinessPartnerCurrentChangeQueryKey,
  getV1BusinessPartnerCurrentOptions as getPmBusinessPartnerCurrentOptions,
  getV1BusinessPartnerCurrentQueryKey as getPmBusinessPartnerCurrentQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import { getErrorStatus } from '../../utils/errorStatus';

/**
 * Query hooks for the current Business Partner's settings (General
 * Information, Accessibility, the organization icon, Deactivate Account) and
 * the Organizations the current Identity belongs to. PAP and PM each expose a
 * `businessPartner/current` resource with a different shape, so both reads
 * live here under their own key. See
 * `./IdentityProviders/identityProviders.queries.ts` for the pattern.
 */

/** Icon URLs in the response are presigned and expire, hence the stale time. */
export const useCurrentBusinessPartner = () =>
  useQuery({
    ...getPapBusinessPartnerCurrentOptions(),
    staleTime: ICON_URL_STALE_TIME,
    refetchOnMount: true,
  });

export const invalidateCurrentBusinessPartner = () =>
  queryClient.invalidateQueries({ queryKey: getPapBusinessPartnerCurrentQueryKey() });

export const useCurrentBusinessPartnerPM = () => useQuery(getPmBusinessPartnerCurrentOptions());

export const invalidateCurrentBusinessPartnerPM = () =>
  queryClient.invalidateQueries({ queryKey: getPmBusinessPartnerCurrentQueryKey() });

/** Resolves to `null` when no change is pending (the API answers 404). */
export const useCurrentBusinessPartnerChangePM = () =>
  useQuery({
    queryKey: getV1BusinessPartnerCurrentChangeQueryKey(),
    queryFn: async ({ signal }) => {
      try {
        const { data } = await getV1BusinessPartnerCurrentChange({ signal, throwOnError: true });
        return data;
      } catch (error) {
        if (getErrorStatus(error) === 404) {
          return null;
        }
        throw error;
      }
    },
  });

export const invalidateCurrentBusinessPartnerChangePM = () =>
  queryClient.invalidateQueries({ queryKey: getV1BusinessPartnerCurrentChangeQueryKey() });

export const useCurrentIdentityBusinessPartners = () =>
  useQuery(getV1IdentityCurrentBusinessPartnersOptions());

export const invalidateCurrentIdentityBusinessPartners = () =>
  queryClient.invalidateQueries({ queryKey: getV1IdentityCurrentBusinessPartnersQueryKey() });
